/**
 * R-236 B3b — OperatorDetail drill-in.
 *
 * Two data sources, two permission tiers:
 * 1. Range numbers (ops_console-gated — always available):
 *    GET /api/ops/staff/historical?operatorId=...&from=...&to=...
 *    Shows operator name, total-docs summary, and per-job-type breakdown table.
 *
 * 2. Trend chart (analytics-gated — may 403):
 *    useOperatorTrend + OperatorTrendChart (reused from OperatorDetailPage).
 *    Graceful degrade: error → "trend unavailable" note; empty → empty-state.
 *    Range numbers always render regardless of trend permission.
 *
 * Design: Industrial Core (DESIGN.md). CSS tokens only — no inline styles.
 * Accent: Safety Orange #ea580c (--color-primary).
 */

import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api-client";
import { useOperatorTrend } from "@/features/analytics/hooks/useOperatorTrend";
import {
  OperatorTrendChart,
  type OperatorTrendMetric,
} from "@/features/analytics/components/OperatorTrendChart";
import { addOneDayIso } from "./dateRange";
import "../admin.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HistoricalRow {
  operatorId: string;
  jobTypeId: string;
  displayName: string;
  jobTypeName: string;
  totalDocs: number;
  docsPerMin: number;
  pagesPerMin: number;
  keysPerMin: number;
  corrections: number;
  avgTimePerField: number;
}

interface OperatorDetailProps {
  operatorId: string;
  from?: string;
  to?: string;
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// OperatorDetail
// ---------------------------------------------------------------------------

export function OperatorDetail({ operatorId, from, to, onBack }: OperatorDetailProps) {
  // ── Range numbers (ops_console tier) ─────────────────────────────────────
  const [rows, setRows] = useState<HistoricalRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!operatorId) return;
    setIsLoading(true);
    setFetchError(null);

    // `to` is the user-facing inclusive end date; the API expects an exclusive
    // upper bound (half-open window), so add one day before sending.
    const apiTo = to ? addOneDayIso(to) : undefined;

    const qs = new URLSearchParams({ operatorId });
    if (from) qs.set("from", from);
    if (apiTo) qs.set("to", apiTo);

    api
      .get<{ rows: HistoricalRow[] }>(`/ops/staff/historical?${qs.toString()}`)
      .then((data) => setRows(data.rows))
      .catch((e) => setFetchError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setIsLoading(false));
  }, [operatorId, from, to]);

  const displayName = rows[0]?.displayName ?? operatorId;
  const totalDocs = useMemo(() => rows.reduce((sum, r) => sum + r.totalDocs, 0), [rows]);

  // ── Trend chart (analytics tier) ─────────────────────────────────────────
  const trend = useOperatorTrend(operatorId, from, to);
  const metric: OperatorTrendMetric = "avgKpm";

  const series = useMemo(() => {
    const points = trend.data?.data ?? [];
    if (points.length === 0) return [];
    return [
      {
        operatorId,
        operatorName: displayName,
        points,
      },
    ];
  }, [trend.data, operatorId, displayName]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="operator-detail-view" data-testid="operator-detail-view">
      {/* Back link */}
      <button type="button" className="admin-back" onClick={onBack} aria-label="All operators">
        ← All operators
      </button>

      {/* Operator heading */}
      <h2 className="od-heading">{displayName}</h2>

      {/* ── Range numbers section ── */}
      {isLoading ? (
        <div data-testid="od-loading" className="admin-loading">
          Loading…
        </div>
      ) : fetchError ? (
        <div className="admin-error">{fetchError}</div>
      ) : (
        <>
          {/* Summary stat cards */}
          <div className="od-stats">
            <div className="od-stat-card">
              <div className="od-stat-value" data-testid="od-total-docs">
                {totalDocs.toLocaleString()}
              </div>
              <div className="od-stat-label">Total Docs</div>
            </div>
          </div>

          {/* Per-job-type breakdown table */}
          {rows.length > 0 ? (
            <div className="od-breakdown-wrap">
              <div className="od-section-title">By Job Type</div>
              <table className="roster-table" data-testid="od-breakdown-table">
                <thead>
                  <tr>
                    <th scope="col">Job Type</th>
                    <th scope="col">Total Docs</th>
                    <th scope="col">Docs/min</th>
                    <th scope="col">Pages/min</th>
                    <th scope="col">Keys/min</th>
                    <th scope="col">Corrections</th>
                    <th scope="col">Avg time/field</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.operatorId}-${row.jobTypeId}`} className="roster-row">
                      <td>{row.jobTypeName}</td>
                      <td>{row.totalDocs.toLocaleString()}</td>
                      <td>{row.docsPerMin.toFixed(1)}</td>
                      <td>{row.pagesPerMin.toFixed(1)}</td>
                      <td>{row.keysPerMin.toFixed(0)}</td>
                      <td>{row.corrections}</td>
                      <td>
                        {row.avgTimePerField > 0 ? `${Math.round(row.avgTimePerField)}ms` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="jt-empty">No data for this range.</div>
          )}
        </>
      )}

      {/* ── Trend chart section (analytics-gated) ── */}
      <div className="od-section-title od-section-title--trend">Trend</div>
      {trend.error ? (
        <p
          className="od-trend-unavailable operator-detail-note"
          data-testid="od-trend-unavailable"
        >
          Trend unavailable — analytics permission required.
        </p>
      ) : (
        <OperatorTrendChart series={series} metric={metric} />
      )}
    </div>
  );
}
